/**
 * `/apply/preview` - "Preview your public page" (design 24, T28; the owner's pick, option 1,
 * 2026-10-11): the author's public page as readers would see it, drawn inside the cabinet from the
 * SAVED profile. The Author profile page links here; "Back to editing" leads back.
 *
 * ★ ANY SIGNED-IN PERSON, LIKE `/apply` ITSELF (`../apply/index.tsx`). It reads only what the
 * application form reads - `getOwnProfile`, and `listEditorCategories` to name the main areas - and
 * both "accept any signed-in Firebase user". So an applicant, who has no public page yet, sees what
 * theirs will be. The router gives it `/apply`'s gate and `/apply`'s frame (`src/router.tsx`, at the
 * foot): inside the shell for an author the cabinet is open to, its own frame otherwise. The one
 * exception is the page's figures (batch 2, B2-PREVIEW): two reads the cabinet already makes, which
 * answer an author row only - anyone else is refused them and sees no figures (./public-figures.ts).
 *
 * ★ SAVED, NOT TYPED. Unsaved changes on the form are not here: a preview of words nobody has saved
 * would show a page readers will never see. In the default build (no server) the fixture starts
 * afresh on this page, as `/apply`'s does on every visit - `?fixture=` picks the scenario, the same.
 *
 * Its option 2, the website's own preview of a not-yet-public profile through a short-lived link,
 * is a later line in the website's brief (`docs/design/20-client-switch-web.md`, "Later").
 */
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createApplicationApi, readFixtureScenario } from '../../features/application';
import type { ApplicationApi } from '../../features/application';
import { PageFrame } from '../../features/application/components/PageFrame';
import { ErrorState, LoadingState } from '../../features/application/components/States';
import type { CategoryList, OwnProfile } from '../../features/application/contract';
import { useAsyncResource } from '../../features/application/data/useAsyncResource';
import { PROFILE_PREVIEW_PATH } from './paths';
import { PreviewBar, ProfilePreview } from './ProfilePreview';
import { usePublicFigures } from './public-figures';
import { publicViewOf } from './public-view';
import { useTranslation } from '../../i18n';
import classes from './profile-preview.module.css';

export { PROFILE_PREVIEW_PATH } from './paths';

/** The route descriptor, `/apply`'s shape: a session, and no author capability. */
export const PROFILE_PREVIEW_ROUTE = {
  path: PROFILE_PREVIEW_PATH,
  requiresAuth: true,
  /** No capability is needed: an applicant may look too. */
  requiresAuthorCapability: false,
} as const;

export interface ProfilePreviewRouteProps {
  /** True when the page is drawn outside the cabinet's shell: an applicant, or the default build. */
  readonly outside: boolean;
  /** The application seam. Omitted - and the router omits it - the app's own, as `/apply` makes it. */
  readonly api?: ApplicationApi | undefined;
}

export function ProfilePreviewRoute({ outside, api: given }: ProfilePreviewRouteProps): ReactNode {
  const { t, i18n } = useTranslation('profile');
  // Created ONCE per mount, as `/apply` does.
  const api = useMemo(() => {
    if (given !== undefined) return given;
    const scenario = readFixtureScenario(window.location.search);
    return createApplicationApi(scenario === undefined ? {} : { scenario });
  }, [given]);

  const profile = useAsyncResource<OwnProfile>(
    useCallback((signal) => api.getOwnProfile({ signal }), [api]),
  );
  // Optional, as on the form: without it a main area is named by its code (./public-view.ts).
  // The names come in the cabinet's language, so a change of language reads them again.
  const categories = useAsyncResource<CategoryList>(
    useCallback((signal) => api.listEditorCategories({ signal }), [api]),
    i18n.language,
  );

  const view = useMemo(
    () => (profile.data === undefined ? undefined : publicViewOf(profile.data, categories.data)),
    [profile.data, categories.data],
  );
  // Never waited for: the page draws without them, and they join it when they answer.
  const figures = usePublicFigures();

  return (
    <PageFrame outside={outside} size="lg">
      <div className={classes.frame}>
        <PreviewBar />
        {profile.status === 'error' && view === undefined ? (
          <ErrorState
            error={profile.error}
            fallback={t('preview.loadFailed')}
            onRetry={profile.reload}
          />
        ) : view === undefined || categories.status === 'loading' ? (
          <LoadingState label={t('preview.loadingWhat')} lines={8} />
        ) : (
          <ProfilePreview view={view} figures={figures} />
        )}
      </div>
    </PageFrame>
  );
}
